import {BaseModuleState} from "../../../../../infrastructure/models/base-module-state";
import {BaseMobxState} from "../../../../../infrastructure/mobx/base-mobx-state";
import {NavigationManager} from "../../../../../core/managers/navigation-manager";
import {AppStore} from "../../../../../core/app/app-store";
import {LoggerUtils} from "../../../../../infrastructure/utils/logger/logger-utils";
import {DatetimeUtils} from "../../../../../infrastructure/utils/datetime-utils";
import {DeviceViewModel} from "../../../../../core/models/device.view-model";
import {TermsUtils} from "../../../../../infrastructure/utils/terms-utils";
import {FullAthleteViewModel} from "../../../../../domain/full-athlete/models/full-athlete/full-athlete.view-model";
import {AthleteNextGameModuleConfig} from "../athlete-next-game.module.config";
import {observable, computed, override, makeObservable} from "mobx";
// import {FullAthleteDataSource} from "../../../../../domain/full-athlete/full-athlete.data-source";
import {ODDS_TYPES} from "../../../../../core/components/odds/components/odds-consts";
import {GameCardViewModel} from "../../../../../core/components/game-card/view-models/game-card.view-model";
import {PredictionSelectionModel} from "../../../../../infrastructure/models/schema/prediction-selection.model";
import {PredictionsSelectionsManager} from "../../../../../core/managers/predictions-selections.manager";
import {AthleteNextGameCardViewModel} from "./athlete.next-game-card.view-model";
import {GAME_STATUSES} from "../../../../../core/app/consts";
import {AthleteNextGamePredictionViewModel} from "./athlete-next-game.prediction-view-model";
import {COMPONENT_STATES} from "../../../../../core/consts";
import {PredictionDetailsPopup} from "../../../../../core/components/prediction/models/assets/prediction-details-popup";
import type {PlayerBetPredictionDomainModel} from "../../../../../domain/full-game/models/player-bet-prediction/player-bet-prediction.domain-model";
import {PropositionBetsDetailsDisplayModel} from "../../../../../core/models/proposition-bets-details/proposition-bets.details.display-model";
import {PredictionContextModel} from "./prediction-context.model";

export class AthleteNextGameModuleState extends BaseModuleState {
  // dataSource: FullAthleteDataSource;
  @observable currentIndex: Number = 0;
  @observable popupDisplay: PropositionBetsDetailsDisplayModel;

  static injectables = [...BaseMobxState.injectables, NavigationManager, AppStore, LoggerUtils, DatetimeUtils, DeviceViewModel, TermsUtils, AthleteNextGameModuleConfig, PredictionsSelectionsManager];

  constructor(
    navigationManager: NavigationManager,
    appStore: AppStore,
    logger: LoggerUtils,
    datetimeUtils: DatetimeUtils,
    device: DeviceViewModel,
    termsUtils: TermsUtils,
    config: AthleteNextGameModuleConfig,
    predictionsSelectionsManager: PredictionsSelectionsManager,
  ) {
    super(navigationManager, appStore, logger, datetimeUtils, device, termsUtils, config);

    makeObservable(this)

    this.predictionsSelectionsManager = predictionsSelectionsManager;
    this.popupDisplay = new PropositionBetsDetailsDisplayModel();
  }

  @override get isPopulated(): Boolean {
    return this.componentState === COMPONENT_STATES.loading || (this.dataSource?.athletes?.length > 0 && this.nextGame);
  }

  @computed get isVisible(): Boolean {
    const isVisible: Boolean = this.popupDisplay?.athleteId > 0 && this.popupDisplay?.lineTypeId > 0 && this.popupDisplay?.gameId > 0;

    return isVisible;
  }

  @computed get currentContext() {
    return new PredictionContextModel();
  }

  @computed get iconSource() {
    return new PredictionDetailsPopup(16,16)
  }

  @computed get cardTitle() {
    if (!this.athlete?.nextGame) {
      return null;
    }

    return this.terms.WEB_ATHLETE_NEXT_GAME_HEADER.format({
      'COMPETITION_NAME': this.athlete.nextGame.competition.name,
      'RELEVANT_GAME': this.statusText
    })
  }

  @computed get statusText() {
    let statusText: String = this.terms.WEB_ATHLETE_NEXT_GAME_STATUS_NEXT;

    if (this.statusGroup === GAME_STATUSES.started) {
      statusText = this.terms.WEB_ATHLETE_NEXT_GAME_STATUS_LIVE
    } else if (this.statusGroup === GAME_STATUSES.ended) {
      statusText = this.terms.WEB_ATHLETE_NEXT_GAME_STATUS_LAST
    }

    return statusText
  }

  @computed get athleteDomainModel() {
    return this.dataSource?.athletes.first()
  }

  @computed get athlete() {
    let athlete: FullAthleteViewModel = null;

    if (this.athleteDomainModel) {
      athlete = new FullAthleteViewModel(this.athleteDomainModel, this.datetimeUtils, this.terms, this.device.isSmallView)
    }

    return athlete;
  }

  @computed get nextGame(): GameCardViewModel {
    let nextGame: GameCardViewModel = null;

    if (this.athlete?.nextGame) {
      const oddsType: ODDS_TYPES = this.appState.currentOddsType;
      const date: String = this.datetimeUtils.toShortNamedDate(this.athlete.nextGame.startTime);
      const time: String = this.datetimeUtils.toShortTime(this.athlete.nextGame.startTime);
      const athleteNotInSquad: Boolean = this.athlete.nextGame.athleteNotInSquad;

      nextGame = new AthleteNextGameCardViewModel(this.athlete.nextGame.domainModel, this.terms, this.device.isSmallView, date, time, oddsType, athleteNotInSquad, this.datetimeUtils);
    }

    return nextGame
  }

  @computed get playersBetsPredictions(): Array<AthleteNextGamePredictionViewModel> {
    const predictions: Array<AthleteNextGamePredictionViewModel> = this.athleteDomainModel?.nextGame.playersBetsPredictions?.map((prediction: PlayerBetPredictionDomainModel, index: Number) => {
      const selection: PredictionSelectionModel = this.selections.find((selection: PredictionSelectionModel) => selection.predictionId === prediction.id);
      prediction.athleteId = this.athlete.id;
      const playerBetPrediction: AthleteNextGamePredictionViewModel = new AthleteNextGamePredictionViewModel(index, prediction, selection?.num, this.selectedOddsType, this.statusGroup, this.gameId);

      return playerBetPrediction
    });

    return predictions;
  }

  @computed get showOdds(): Boolean {
    const showOdds: Boolean = this.appState.showOdds || this.appState.showSecondaryOdds;

    return showOdds;
  }

  @computed get selectedPrediction(): AthleteNextGamePredictionViewModel {
    return this.playersBetsPredictions?.first();
  }

  @computed get selectedBookmaker(): AthleteNextGamePredictionViewModel {
    return this.selectedPrediction?.odds?.bookmaker;
  }

  @computed get selections(): Array<PredictionSelectionModel> {
    return this.predictionsSelectionsManager.selections;
  }

  @computed get selectedOddsType(): ODDS_TYPES {
    return this.appState.currentOddsType;
  }

  @computed get competitionId(): Date {
    return this.nextGame?.competitionId;
  }

  @computed get statusGroup(): Number {
    return this.nextGame?.statusGroup
  }

  @computed get date(): Date {
    return this.nextGame?.startTime;
  }

  @computed get gameId(): Number {
    return this.nextGame?.id
  }

  @computed get isInnerLinksBlocked(): Boolean {
    const isInnerLinksBlocked: Boolean = this.appState.settings.isWidget && this.appState.settings.bootstrapSettings.blockInnerLinks;

    return isInnerLinksBlocked;
  }

  @computed get sportTypeId(): Number {
    return this.nextGame?.sportId;
  }
}
